import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import i18n from '../i18n'

const UploadPortalContext = createContext(null)

const SESSION_KEY = 'upload_portal_auth'

const initialState = {
  authenticated: false,
  caseReference: '',
  email: '',
  checklist: null,
  documents: [],
  restoring: true,
}

function loadFromStorage() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    if (raw) return JSON.parse(raw)
  } catch {}
  return null
}

export function UploadPortalProvider({ children }) {
  const [data, setData] = useState(() => {
    const stored = loadFromStorage()
    if (stored) return { ...initialState, ...stored, documents: [], restoring: true }
    return initialState
  })

  useEffect(() => {
    if (!data.restoring) return
    const stored = loadFromStorage()
    if (!stored) {
      setData((prev) => ({ ...prev, restoring: false }))
      return
    }
    fetch('/api/uploadportal/sessioninfo')
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((json) => {
        if (json.locale) i18n.changeLanguage(json.locale === 'de' ? 'de' : 'en')
        setData((prev) => ({
          ...prev,
          authenticated: true,
          checklist: json.checklist,
          documents: json.documents ?? [],
          restoring: false,
        }))
      })
      .catch(() => {
        sessionStorage.removeItem(SESSION_KEY)
        setData(({ ...initialState, restoring: false }))
      })
  }, [])

  const login = useCallback((caseReference, email, checklist) => {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ caseReference, email }))
    setData((prev) => ({ ...prev, authenticated: true, caseReference, email, checklist, restoring: false }))
  }, [])

  const logout = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY)
    setData({ ...initialState, restoring: false })
  }, [])

  const addDocuments = useCallback((docs) => {
    setData((prev) => ({ ...prev, documents: [...prev.documents, ...docs] }))
  }, [])

  const removeDocument = useCallback((id) => {
    setData((prev) => ({ ...prev, documents: prev.documents.filter((d) => d.id !== id) }))
  }, [])

  const value = useMemo(() => ({ data, login, logout, addDocuments, removeDocument }), [data, login, logout, addDocuments, removeDocument])

  return (
    <UploadPortalContext.Provider value={value}>
      {children}
    </UploadPortalContext.Provider>
  )
}

export function useUploadPortal() {
  const ctx = useContext(UploadPortalContext)
  if (!ctx) throw new Error('useUploadPortal must be used within UploadPortalProvider')
  return ctx
}
